import React, { useState, useRef, useEffect } from 'react';
import { SUPPORTED_TOKENS } from '@/config/tokens';
import TokenIcon from '@/components/ui/TokenIcon';

interface TokenSelectorProps {
  selectedToken: string;
  onTokenSelect: (token: string) => void;
  className?: string;
  style?: React.CSSProperties;
  iconSize?: number;
}

const TokenSelector: React.FC<TokenSelectorProps> = ({ 
  selectedToken, 
  onTokenSelect, 
  className,
  style,
  iconSize = 20
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedTokenData = SUPPORTED_TOKENS[selectedToken as keyof typeof SUPPORTED_TOKENS];

  const handleTokenSelect = (tokenSymbol: string) => {
    onTokenSelect(tokenSymbol);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        className={`${className} inline-flex items-center`}
        style={style}
        onClick={() => setIsOpen(!isOpen)}
      >
        <TokenIcon 
          symbol={selectedToken}
          size={iconSize}
          className="flex-shrink-0"
          alt={selectedTokenData.name}
        />
        <span className="font-medium text-xs sm:text-sm text-white whitespace-nowrap">{selectedTokenData.symbol}</span>
        <img src="/images/arrow-down-2-white.svg" alt="" className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
      </div>
      
      {isOpen && (
        <div 
          className="absolute top-full left-0 mt-2 inline-block w-max whitespace-nowrap bg-black/80 backdrop-blur-lg shadow-xl z-[9999]"
          style={{
            borderRadius: '20px'
          }}
        >
          {(Object.entries(SUPPORTED_TOKENS) as [string, typeof SUPPORTED_TOKENS[keyof typeof SUPPORTED_TOKENS]][]).map(([symbol, token]) => (
            <div
              key={symbol}
              className="flex items-center space-x-3 px-4 py-3 hover:bg-white/10 cursor-pointer transition-colors first:rounded-t-[20px] last:rounded-b-[20px]"
              onClick={() => handleTokenSelect(symbol)}
            >
              <TokenIcon 
                symbol={symbol}
                size={20}
                className="flex-shrink-0"
                alt={token.name}
              />
              <div className="flex flex-col">
                <span className="font-medium text-sm text-white">{token.symbol}</span>
                <span className="text-xs text-white/70">{token.name}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TokenSelector;